import { setCursor } from '../../lib/cursor';
import {
  addElement,
  defaultGenericElement,
  reversionElement,
  updateElement,
  type GenericElement,
} from '../../lib/element';
import { type Plugin } from '../../lib/plugin';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { updateEditorStore } from '../../stores/useEditorStore';
import { hexToRgbString } from '../../utils/color';
import { type Point } from '../../utils/math';
import { general } from '../general';
import { type GenericPolyElement, poly } from '../poly';
import { getPathFromPoints } from '../poly/utils/path';
import {
  getPolyShape,
  getEllipsePoints,
  getEllipseBezierHandles,
} from '../poly/utils/shape';

const MIN_AUTO_SIZE = 100;

export type EllipsePlugin = Plugin<
  GenericPolyElement,
  typeof actions,
  typeof state
>;

export interface EllipseElement extends GenericPolyElement {
  type: 'ellipse';
}

const state = {
  newStrokeColor: '#3c87f7',
  newStrokeWidth: 4,
};

const actions = {
  activate: (color: string) => {
    setCursor(plugin.cursor);
    general.actions.activate(plugin.name);
    state.newStrokeColor = color;
  },
  setStrokeColor: (color: string) => {
    state.newStrokeColor = color;
  },
  setStrokeWidth: (width: number) => {
    state.newStrokeWidth = width;
  },
};

const plugin: EllipsePlugin = {
  name: 'ellipse',
  cursor: 'crosshair',
  actions,
  state,
  events: {
    onPointerDown: ({ event, helpers, runtimeState }) => {
      const { button, pos } = event;

      if (button === 'left' && runtimeState.activeTool === 'ellipse') {
        const newElement: EllipseElement = {
          ...defaultGenericElement(),
          originX: pos.x,
          originY: pos.y,
          points: [
            [0, 0],
            [0, 0],
            [0, 0],
            [0, 0],
          ],
          path: new Path2D(),
          strokeColor: state.newStrokeColor,
          strokeWidth: state.newStrokeWidth,
          type: 'ellipse',
          x: pos.x,
          y: pos.y,
        };

        helpers.updateRuntimeStore({
          newElement: newElement,
          originalNewElement: { ...newElement } as GenericElement,
        });
        helpers.showNewElementCanvas();
        helpers.renderNewElementScene();
      }
    },
    onPointerMove: ({ event, helpers, runtimeState }) => {
      const { button, pos } = event;

      if (
        button === 'left' &&
        runtimeState.newElement &&
        runtimeState.newElement.type === 'ellipse'
      ) {
        const element = runtimeState.newElement as EllipseElement;
        const originalElement =
          runtimeState.originalNewElement as EllipseElement;

        const originPosition = [originalElement.x, originalElement.y] as Point;
        let newWidth = Math.abs(pos.x - originPosition[0]);
        let newHeight = Math.abs(pos.y - originPosition[1]);
        const newX = Math.min(pos.x, originPosition[0]);
        const newY = Math.min(pos.y, originPosition[1]);

        if (runtimeState.activeKeyPress?.shift) {
          newHeight = newWidth = Math.min(newWidth, newHeight);
        }
        const newPoints = getEllipsePoints(newWidth, newHeight);
        const handlesPointBezierHandle = getEllipseBezierHandles(
          newWidth,
          newHeight,
        );

        const newElement = {
          ...element,
          x: newX,
          y: newY,
          bezierHandles: handlesPointBezierHandle,
          width: newWidth,
          height: newHeight,
          points: newPoints,
        } as GenericPolyElement;

        newElement.shape = getPolyShape(newElement);
        newElement.version = {
          ...newElement.version,
          ...reversionElement(element, ['bounds', 'canvas']).version,
        };

        helpers.updateRuntimeStore({ newElement });
        helpers.renderNewElementScene();
      }
    },
    onPointerUp: ({ elementState, event, helpers, history, runtimeState }) => {
      if (!runtimeState.newElement || runtimeState.activeTool !== 'ellipse')
        return;

      let element = runtimeState.newElement as GenericPolyElement;

      if (element.width === 0 || element.height === 0) {
        const centeredX = event.pos.x - MIN_AUTO_SIZE / 2;
        const centeredY = event.pos.y - MIN_AUTO_SIZE / 2;
        const newPoints = getEllipsePoints(MIN_AUTO_SIZE, MIN_AUTO_SIZE);
        const handlesPointBezierHandle = getEllipseBezierHandles(
          MIN_AUTO_SIZE,
          MIN_AUTO_SIZE,
        );

        element = {
          ...element,
          x: centeredX,
          y: centeredY,
          originX: centeredX,
          originY: centeredY,
          width: MIN_AUTO_SIZE,
          height: MIN_AUTO_SIZE,
          points: newPoints,
          bezierHandles: handlesPointBezierHandle,
        };

        element.shape = getPolyShape(element);
        element.version = {
          ...element.version,
          ...reversionElement(element, ['bounds', 'canvas']).version,
        };
      } else {
        element.shape = getPolyShape(element);
      }

      addElement(elementState.elements, element);
      history.push(updateEditorStore({ selectedElementIds: [element.id] }));
      updateRuntimeStore({
        activeTool: 'selection',
        newElement: undefined,
        originalNewElement: undefined,
      });

      helpers.renderStaticScene();
      helpers.renderInteractiveScene();
      helpers.hideNewElementCanvas();
    },
    blockTouchPanning: true,
  },
  render: {
    padding: (element) => element.strokeWidth / 2,
    draw: ({
      config,
      context,
      element,
      runtimeState,
      elementState,
      skipElementUpdate,
    }) => {
      if (!runtimeState.newElement && !element.points.length) return;
      if (element.renderedById != null) return;
      context.save();

      context.lineJoin = 'round';
      context.lineCap = 'round';
      context.lineWidth = element.strokeWidth / 2;
      context.fillStyle = hexToRgbString(element.strokeColor, 0.8);

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      const path = getPathFromPoints(
        element.points,
        element.bezierHandles,
        !element.isOpen,
      );

      context.fill(path);

      if (!skipElementUpdate) {
        updateElement<GenericPolyElement>(elementState.elements, element, {
          path,
        });
      }
      context.restore();
    },
  },
  collision: poly.collision,
  selection: {
    renderHoverBorder: poly.selection?.renderHoverBorder,
  },
  transformation: {
    shiftBySizeOnFlip: poly.transformation.shiftBySizeOnFlip,
    onSingleResize: poly.transformation.onSingleResize,
    onMultiResize: poly.transformation.onMultiResize,
    resizedBounds: poly.transformation.resizedBounds,
    transformElement: poly.transformation.transformElement,
    manualRotation: poly.transformation.manualRotation,
  },
  export: poly.export,
  import: poly.import,
};

export const ellipse = plugin;
